import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { authService } from '../services/apiService';
import { refreshCookieSession, logoutCookieSession } from '../services/loginOtpApi';
import { hasCookieSessionHint, markCookieSessionActive, clearCookieSessionHint } from './authSessionHint';
import api from '../utils/api';

export const AuthContext = createContext(null);

// User roles
export const USER_ROLES = {
  CUSTOMER: 'customer',
  VENDOR: 'vendor',
  USED_VENDOR: 'used_vendor',
  USED_PENDING: 'used_pending',
  MECHANICS: 'mechanics',
  /** Full admin panel access (matches backend authorize("superadmin") which allows admin + superadmin). */
  SUPER_ADMIN: 'superadmin',
  ADMIN: 'admin',
  GARAGE: 'garage',
  SHIPPING: 'shipping'
};

/** Normalize role strings from API (e.g. super_admin, super-admin → superadmin) for routing and access checks. */
export function normalizeUserRoleKey(role) {
  return (role || '').toString().trim().toLowerCase().replace(/[_\s-]+/g, '');
}

function extractUserFromProfile(profile) {
  let user =
    profile?.user ||
    profile?.data?.user ||
    profile?.data?.data?.user ||
    null;
  if (user?.user && typeof user.user === 'object' && !user.role) {
    user = user.user;
  }
  return user;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state on mount (legacy bearer token or B-01B httpOnly cookies)
  useEffect(() => {
    const applyProfileUser = (profile, fallbackUser = null) => {
      const user = extractUserFromProfile(profile) || fallbackUser;
      if (!user) return false;
      const merged = fallbackUser?.role && !user.role ? { ...user, role: fallbackUser.role } : user;
      setUser(merged);
      setIsAuthenticated(true);
      return true;
    };

    const fetchProfileFromApi = async () => {
      const response = await api.get('/auth/profile', { skipAuthRefresh: true });
      const payload = response.data?.data || response.data;
      return { user: payload?.user || payload };
    };

    const restoreCookieSession = async () => {
      if (!hasCookieSessionHint()) {
        return false;
      }

      try {
        const profile = await fetchProfileFromApi();
        return applyProfileUser(profile);
      } catch (error) {
        const status = error.response?.status;
        if (status !== 401 && status !== 403) {
          console.warn('Profile fetch failed during cookie restore:', error);
          return false;
        }
        try {
          await refreshCookieSession();
          const profile = await fetchProfileFromApi();
          return applyProfileUser(profile);
        } catch (_) {
          clearCookieSessionHint();
          return false;
        }
      }
    };

    const initializeAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('authToken');

        if (token && storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);

          authService
            .getProfile()
            .then((profile) => {
              applyProfileUser(profile, userData);
            })
            .catch((error) => {
              if (error.response?.status === 401 || error.response?.status === 403) {
                console.error('Token validation failed:', error);
                localStorage.removeItem('user');
                localStorage.removeItem('authToken');
                localStorage.removeItem('userRole');
                setUser(null);
                setIsAuthenticated(false);
              } else {
                console.warn('Profile fetch failed, using cached user:', error);
              }
            });
        } else if (hasCookieSessionHint()) {
          const restored = await restoreCookieSession();
          if (!restored) {
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        if (err.message?.includes('JSON')) {
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
          localStorage.removeItem('userRole');
        }
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for logout events (from API interceptor)
    const handleLogout = () => {
      setUser(null);
      setIsAuthenticated(false);
      setError('Session expired. Please login again.');
    };

    window.addEventListener('logout', handleLogout);
    return () => window.removeEventListener('logout', handleLogout);
  }, []);

  /**
   * Register new user (fresh flow: new user only, no old session data)
   */
  const register = useCallback(async (name, email, password, role, extras = null) => {
    try {
      setLoading(true);
      setError(null);
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');

      const phone = extras?.phone ? String(extras.phone).replace(/\D/g, '').slice(-10) : '';
      const vendorDetails = extras?.vendorDetails ?? (extras?.phone ? null : extras);

      if (!name || !email || !role) {
        throw new Error('All fields are required');
      }
      if (!password && !phone) {
        throw new Error('Password or mobile number is required');
      }
      if (password && password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      const roleToUse = (role && role !== 'pending') ? role : 'pending';
      if (roleToUse !== 'pending' && !Object.values(USER_ROLES).includes(roleToUse)) {
        throw new Error('Invalid role selected');
      }

      const userData = {
        name,
        email,
        role: roleToUse,
        ...(password ? { password } : {}),
        ...(phone ? { phone } : {}),
        ...(extras?.onboardingComplete === false ? { onboardingComplete: false } : {}),
        ...(extras?.authNamespace ? { authNamespace: extras.authNamespace } : {}),
      };
      if (vendorDetails) userData.vendorDetails = vendorDetails;

      const result = await authService.register(userData);
      const newUser = result?.user || result?.data?.user;
      if (!newUser) throw new Error('Registration succeeded but no user returned');

      setUser(newUser);
      setIsAuthenticated(true);
      return result;
    } catch (err) {
      const errorMsg = err.message || 'Registration failed';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login user
   */
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      const result = await authService.login(email, password);
      let user = result.user || result.data?.user || result;
      setUser(user);
      setIsAuthenticated(true);
      if (result.token) {
        localStorage.setItem('authToken', result.token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user?.role || '');
      }
      try {
        const profile = await authService.getProfile();
        const profileUser = profile.data?.data?.user || profile.data?.user || profile.data || profile.user || profile;
        if (profileUser && (profileUser.name !== undefined || profileUser.role !== undefined || profileUser.fullName !== undefined)) {
          user = { ...user, ...profileUser, role: profileUser.role ?? user?.role };
          setUser(user);
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('userRole', user.role || '');
        }
      } catch (_) { /* use login response if profile fails */ }
      if (typeof window !== 'undefined') {
        const { notifyAuthStateChange } = require('../contexts/CartContext');
        notifyAuthStateChange();
      }
      return { user, token: result.token };
    } catch (err) {
      const errorMsg = err.message || 'Login failed';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Establish session after B-01B cookie-based OTP verify.
   * Tokens live in httpOnly cookies — not localStorage.
   */
  const establishCookieSession = useCallback(({ userId, displayName, isNewUser }) => {
    markCookieSessionActive();
    setError(null);
    const userPayload = {
      id: userId,
      _id: userId,
      name: displayName || undefined,
      displayName: displayName || undefined,
      role: USER_ROLES.CUSTOMER,
      onboardingComplete: !isNewUser,
    };
    setUser(userPayload);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(userPayload));
    localStorage.setItem('userRole', USER_ROLES.CUSTOMER);
  }, []);

  /**
   * Phone OTP login
   */
  const phoneLogin = useCallback(async (mobileNumber, otp) => {
    try {
      setLoading(true);
      setError(null);
      const result = await authService.phoneLogin(mobileNumber, otp);
      let user = result.user;
      setUser(user);
      setIsAuthenticated(true);
      if (result.token) {
        localStorage.setItem('authToken', result.token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user?.role || 'pending');
      }
      try {
        const profile = await authService.getProfile();
        const profileUser = profile.data?.data?.user || profile.data?.user || profile.data || profile.user || profile;
        if (profileUser) {
          user = { ...user, ...profileUser, role: profileUser.role ?? user?.role };
          setUser(user);
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('userRole', user.role || '');
        }
      } catch (_) { /* use login response if profile fails */ }
      return result;
    } catch (err) {
      const errorMsg = err.message || 'Phone login failed';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Google OAuth login
   */
  const googleLogin = useCallback(async (googleToken) => {
    try {
      setLoading(true);
      setError(null);
      const result = await authService.googleLogin(googleToken);
      let user = result.user;
      setUser(user);
      setIsAuthenticated(true);
      if (result.token) {
        localStorage.setItem('authToken', result.token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user?.role || 'pending');
      }
      try {
        const profile = await authService.getProfile();
        const profileUser = profile.data?.data?.user || profile.data?.user || profile.data || profile.user || profile;
        if (profileUser) {
          user = { ...user, ...profileUser, role: profileUser.role ?? user?.role };
          setUser(user);
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('userRole', user.role || '');
        }
      } catch (_) { /* use login response if profile fails */ }
      return result;
    } catch (err) {
      const errorMsg = err.message || 'Google login failed';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Facebook OAuth login
   */
  const facebookLogin = useCallback(async (accessToken) => {
    try {
      setLoading(true);
      setError(null);
      const result = await authService.facebookLogin(accessToken);
      let user = result.user;
      setUser(user);
      setIsAuthenticated(true);
      if (result.token) {
        localStorage.setItem('authToken', result.token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user?.role || 'pending');
      }
      try {
        const profile = await authService.getProfile();
        const profileUser = profile.data?.data?.user || profile.data?.user || profile.data || profile.user || profile;
        if (profileUser) {
          user = { ...user, ...profileUser, role: profileUser.role ?? user?.role };
          setUser(user);
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('userRole', user.role || '');
        }
      } catch (_) { /* use login response if profile fails */ }
      return result;
    } catch (err) {
      const errorMsg = err.message || 'Facebook login failed';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get current user profile
   */
  const getProfile = useCallback(async () => {
    try {
      setLoading(true);
      const result = await authService.getProfile();
      // Extract user from result (handle different response structures)
      const userData = result.data?.user || result.user || result.data || result;
      setUser(userData);
      
      // Update localStorage
      if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('userRole', userData.role);
      }
      
      return result;
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update user in context (called after profile update)
   */
  const updateUser = useCallback((userData) => {
    if (!userData) return;
    setUser((prev) => {
      let storedUser = null;
      try {
        const raw = localStorage.getItem('user');
        storedUser = raw ? JSON.parse(raw) : null;
        if (storedUser?.user && typeof storedUser.user === 'object' && !storedUser.role) {
          storedUser = storedUser.user;
        }
      } catch (_) {
        storedUser = null;
      }
      const storedRole = localStorage.getItem('userRole');
      const safeStoredRole =
        storedRole && storedRole !== 'undefined' ? storedRole : null;
      const merged = {
        ...(storedUser && typeof storedUser === 'object' ? storedUser : {}),
        ...(prev && typeof prev === 'object' ? prev : {}),
        ...userData,
        role:
          userData.role ||
          prev?.role ||
          storedUser?.role ||
          safeStoredRole ||
          undefined
      };
      localStorage.setItem('user', JSON.stringify(merged));
      if (merged.role) {
        localStorage.setItem('userRole', merged.role);
      }
      return merged;
    });
  }, []);

  /**
   * Logout user (clears httpOnly cookies + legacy localStorage session)
   */
  const logout = useCallback(async () => {
    try {
      // Always hit the server logout so it can revoke this device's session
      // (bearer jti + cookie session). The bearer token is still in storage here,
      // so the request interceptor attaches it before we clear anything.
      await logoutCookieSession().catch(() => {});
      clearCookieSessionHint();
      authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  }, []);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get dashboard path based on role (always redirect to role dashboard after login).
   * Normalizes API variants: e.g. super_admin, super-admin → superadmin.
   */
  const getDashboardPath = useCallback((role) => {
    const normalized = normalizeUserRoleKey(role);
    const paths = {
      customer: '/customer/dashboard',
      vendor: '/vendor/dashboard',
      used_vendor: '/vendor/dashboard',
      usedpending: '/signup/pending',
      used_pending: '/signup/pending',
      mechanics: '/mechanics/dashboard',
      mechanic: '/mechanics/dashboard',
      superadmin: '/admin/dashboard',
      admin: '/admin/dashboard',
      garage: '/garage/dashboard',
      shipping: '/shipping/dashboard',
      delivery: '/shipping/dashboard',
    };
    return paths[normalized] || '/';
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    register,
    login,
    phoneLogin,
    establishCookieSession,
    googleLogin,
    facebookLogin,
    logout,
    getProfile,
    updateUser,
    clearError,
    getDashboardPath,
    isAuthenticated,
    isCustomer: user?.role === USER_ROLES.CUSTOMER,
    isVendor: user?.role === USER_ROLES.VENDOR,
    isMechanics: user?.role === USER_ROLES.MECHANICS,
    isSuperAdmin: user?.role === USER_ROLES.SUPER_ADMIN,
    isGarage: user?.role === USER_ROLES.GARAGE,
    isShipping: user?.role === USER_ROLES.SHIPPING,
  }), [user, loading, error, isAuthenticated, register, login, phoneLogin, establishCookieSession, googleLogin, facebookLogin, logout, getProfile, updateUser, clearError, getDashboardPath]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
